import { registerLocaleData } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withJsonpSupport,
} from '@angular/common/http';
import localeJa from '@angular/common/locales/ja';
import localeKo from '@angular/common/locales/ko';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { COMPOSITION_BUFFER_MODE } from '@angular/forms';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { RxStomp } from '@stomp/rx-stomp';
import { authInterceptor } from 'src/lib/auth/auth.interceptor-fn';
import {
  DIALOG_SERVICE,
  LOGIN_MODAL_COMPONENT,
} from 'src/lib/guards/login.guard';
import {
  BRAND_IGNORE_PATH_LIST,
  brandInterceptor,
} from 'src/lib/interceptor/brand.interceptor';
import { loggingInterceptor } from 'src/lib/interceptor/logging.interceptor';
import {
  PW_UPDATE_OPTION,
  PwUpdateOption,
} from 'src/lib/services/app-update.service';
import {
  PW_STORAGE_PREFIX,
  StorageService,
} from 'src/lib/services/storage.service';
import { TranslateVersionLoader } from 'src/lib/translate-version-loader';
import { register as registerSwiper } from 'swiper/element';
import { AuthService } from '../lib/auth/auth.service';
import { originInterceptor } from '../lib/interceptor/origin.interceptor';
import { PlatformService } from '../lib/services/platform.service';
import { RxStompService } from '../lib/services/rx-stomp.service';
import { rxStompConfig } from '../rx-stomp.config';
import { routes } from './app.routes';
import { LoginComponent } from './components/dialog/login/login.component';
import { TemplateLayoutModule } from './components/template-layout/template-layout.module';
import { DialogService } from './services/dialog.service';
import { BookingPackagegoodsStateItem } from './states/booking-packagegoods-list.state-item';
import { HomeLayoutBbsStateItem } from './states/home-layout-bbs.state-item';
import { HomeLayoutGoodsStateItem } from './states/home-layout-goods.state-item';
import { HomeLayoutMrhstStateItem } from './states/home-layout-mrhst.state-item';
import { HomeLayoutPackagegoodsStateItem } from './states/home-layout-packagegoods.state-item';
import { HomeLayoutPartnersStateItem } from './states/home-layout-partners.state-item';

registerSwiper();

registerLocaleData(localeKo);
registerLocaleData(localeJa);

const pwUpdateOption: PwUpdateOption = {
  messageTranslateKey: 'updateMessage',
  actionTranslateKey: 'updateAction',
};

const rxStompServiceFactory = (
  authService: AuthService,
  platformService: PlatformService,
): RxStomp => {
  const rxStomp = new RxStompService(authService);
  rxStomp.configure(rxStompConfig);

  // 브라우저 환경에서만 활성화
  if (platformService.isPlatformBrowser) {
    rxStomp.activate();
  }
  return rxStomp;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        get scrollPositionRestoration(): 'disabled' | 'enabled' | 'top' {
          // 브라우저 환경에서만 window 객체 접근
          if (typeof window !== 'undefined') {
            return window.history.scrollRestoration === 'manual'
              ? 'enabled'
              : 'disabled';
          }
          return 'disabled'; // 서버 환경에서는 기본값
        },
      }),
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: typeof window !== 'undefined', // 브라우저 환경에서만 활성화
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(
      // withFetch(),
      withJsonpSupport(),
      withInterceptors([
        originInterceptor,
        loggingInterceptor,
        authInterceptor,
        brandInterceptor,
      ]),
    ),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (httpClient: HttpClient) =>
            new TranslateVersionLoader(httpClient, ['ko', 'en', 'ja']),
          deps: [HttpClient],
        },
        useDefaultLang: false,
      }),
      TemplateLayoutModule,
    ]),
    provideClientHydration(),
    { provide: BRAND_IGNORE_PATH_LIST, useValue: [] },
    { provide: COMPOSITION_BUFFER_MODE, useValue: false },
    // TODO: 추후 직접 변경 가능하게 개선 필요
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'KRW' },
    // LocalStorage 설정
    { provide: PW_STORAGE_PREFIX, useValue: 'TOURNITY' },
    { provide: LOGIN_MODAL_COMPONENT, useValue: LoginComponent },
    { provide: DIALOG_SERVICE, useValue: DialogService },
    // {
    //   provide: MAT_DATE_LOCALE,
    //   useFactory: (lang: LanguageService) => {
    //     return lang.lang;
    //   },
    //   deps: [LanguageService],
    // },
    { provide: PW_UPDATE_OPTION, useValue: pwUpdateOption },
    {
      provide: RxStompService,
      useFactory: (
        authService: AuthService,
        platformService: PlatformService,
      ): RxStomp => {
        return rxStompServiceFactory(authService, platformService);
      },
      deps: [AuthService, PlatformService],
    },
    {
      provide: LOCALE_ID,
      useFactory: (storageService: StorageService): string => {
        return storageService.get('lang') ?? 'ko';
      },
      deps: [StorageService],
    },
    HomeLayoutPackagegoodsStateItem,
    HomeLayoutMrhstStateItem,
    HomeLayoutGoodsStateItem,
    HomeLayoutPartnersStateItem,
    HomeLayoutBbsStateItem,
    BookingPackagegoodsStateItem,
  ],
};
